import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, KeyRound, Check, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Spinner } from "../components/common";

/* Spec §17 — Ownership Transfer accept page.
   Opened from the invitation email link. Public verify → requires sign-in
   as the recipient email → requires fresh re-auth → adult confirm → accept. */
export default function OwnershipAccept() {
  const { user, logout } = useAuth() || {};
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [verified, setVerified] = useState(null); // null=loading, false=bad, obj=ok
  const [err, setErr] = useState(null);
  const [adult, setAdult] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!token) { setErr("Missing token"); setVerified(false); return; }
    api.post("/ownership/verify", { token })
      .then((r) => setVerified(r.data))
      .catch((e) => { setErr(e?.response?.data?.detail || "Invalid or expired link"); setVerified(false); });
  }, [token]);

  const doReauth = async () => {
    toast.info("Signing you out — please sign back in, then reopen this invitation link.");
    try { if (typeof logout === "function") await logout(); }
    catch { /* noop */ }
    setTimeout(() => nav(`/login?next=/settings/ownership-transfer/accept?token=${token}`), 900);
  };

  const accept = async () => {
    if (!adult) { toast.error("Please confirm both parties are adults over 18"); return; }
    setBusy(true);
    try {
      const r = await api.post("/ownership/accept", { token, confirm_adult: true });
      setDone(r.data);
      toast.success("Transfer complete — everything is now under your account");
      setTimeout(() => nav("/travel-hub"), 3200);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not accept";
      toast.error(msg);
      if (e?.response?.status === 401) await doReauth();
    } finally { setBusy(false); }
  };

  if (verified === null) return <Spinner />;

  if (!verified) return (
    <div className="max-w-lg mx-auto px-5 py-16 text-center" data-testid="ownership-accept-invalid">
      <ShieldCheck className="w-10 h-10 mx-auto text-slate-400 mb-3" />
      <h1 className="font-serif font-800 text-3xl">Link no longer works</h1>
      <p className="text-slate-600 mt-2 text-sm">{err || "This transfer link is invalid, cancelled or expired."}</p>
      <Link to="/travel-hub" className="mt-6 inline-block text-blue-700 underline">Back to My Travel Hub</Link>
    </div>
  );

  const emailMismatch = user && (user.email || "").toLowerCase() !== verified.recipient_email;

  return (
    <div className="max-w-2xl mx-auto px-5 py-8" data-testid="ownership-accept-page">
      <Link to="/travel-hub" className="inline-flex items-center gap-1 text-blue-700 text-sm hover:underline mb-3">
        <ArrowLeft className="w-4 h-4" /> Cancel
      </Link>
      <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-emerald-600 flex items-center gap-1.5">
        <ShieldCheck className="w-3.5 h-3.5" /> Ownership transfer · Accept
      </div>
      <h1 className="font-serif font-800 text-4xl text-slate-900 mt-1">
        <span className="text-emerald-700">{verified.owner_name || verified.owner_email}</span> wants to move their entire Hub to you
      </h1>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-700" data-testid="ownership-accept-summary">
        <div className="grid grid-cols-2 gap-4">
          <div><div className="text-[10px] font-800 uppercase tracking-widest text-slate-500">From</div>
            <div className="mt-1 font-700 text-slate-900">{verified.owner_email}</div></div>
          <div><div className="text-[10px] font-800 uppercase tracking-widest text-slate-500">To</div>
            <div className="mt-1 font-700 text-slate-900">{verified.recipient_email}</div></div>
        </div>
        {verified.note && (
          <blockquote className="mt-4 border-l-4 border-slate-300 pl-3 text-slate-600 italic">
            {verified.note}
          </blockquote>
        )}
        <p className="mt-4 text-xs text-slate-500">
          Expires {(verified.expires_at || "").slice(0, 16).replace("T", " ")}
        </p>
      </div>

      <div className="mt-6 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
        Every trip, traveler, membership, loyalty record, Inner Circle entry and vault item
        currently owned by <b>{verified.owner_email}</b> will move to your account permanently.
      </div>

      {!user ? (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="ownership-accept-signin">
          <p className="text-sm text-slate-700">Please sign in as <b>{verified.recipient_email}</b> to review and accept.</p>
          <Link to={`/login?next=/settings/ownership-transfer/accept?token=${token}`}
            className="mt-3 inline-flex items-center gap-1.5 bg-emerald-600 text-white text-sm font-800 px-4 py-2.5 hover:bg-emerald-700">
            Sign in to continue
          </Link>
        </div>
      ) : emailMismatch ? (
        <div className="mt-6 rounded-2xl border border-rose-300 bg-rose-50 p-5 text-sm text-rose-800" data-testid="ownership-accept-mismatch">
          You're signed in as <b>{user.email}</b> but this invitation is for <b>{verified.recipient_email}</b>.
          Please sign out and sign back in as the intended recipient.
        </div>
      ) : done ? (
        <div className="mt-6 rounded-2xl border border-emerald-300 bg-emerald-50 p-5" data-testid="ownership-accept-done">
          <div className="text-emerald-800 font-800 flex items-center gap-2"><Check className="w-5 h-5" /> Transfer complete</div>
          <div className="text-sm text-emerald-900 mt-1">
            {Object.keys(done.moved || {}).length} categor{Object.keys(done.moved || {}).length === 1 ? "y" : "ies"} moved to your account. Redirecting…
          </div>
        </div>
      ) : (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="ownership-accept-confirm">
          <button
            type="button"
            onClick={doReauth}
            data-testid="ownership-accept-reauth"
            className="mb-4 inline-flex items-center gap-1.5 border border-slate-300 text-slate-700 px-3 py-2 text-xs font-700 hover:bg-slate-50">
            <KeyRound className="w-4 h-4" /> Re-authenticate (required in the last 10 min)
          </button>

          <label className="flex items-start gap-2 text-sm text-slate-800 mb-4">
            <input
              type="checkbox"
              checked={adult}
              onChange={(e) => setAdult(e.target.checked)}
              data-testid="ownership-accept-adult"
              className="mt-1"
            />
            <span>I confirm both my account and the sender's account belong to adults over 18, and I accept full ownership of the transferred workspace.</span>
          </label>

          <button
            onClick={accept}
            disabled={!adult || busy}
            data-testid="ownership-accept-btn"
            className="inline-flex items-center gap-2 bg-emerald-600 text-white font-800 px-5 py-3 text-sm hover:bg-emerald-700 disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Accept ownership transfer
          </button>
        </div>
      )}
    </div>
  );
}
